import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Check, Loader2, X, Plus } from "lucide-react";
import { statusApi } from "../../api/status";

interface SentryConfigOut {
  platform: string;
  auth_token: string | null;
  org_slug: string | null;
  project_slugs: string | null;
  is_configured: boolean;
  updated_at: string | null;
}

type SaveState = "idle" | "saving" | "saved" | "error";

interface PlatformFormState {
  auth_token: string;
  org_slug: string;
  project_slugs: string[];  // array of individual slugs
  slugInput: string;        // current text in the tag input
}

function slugsFromString(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function ProjectSlugInput({
  slugs,
  input,
  onInputChange,
  onAdd,
  onRemove,
}: {
  slugs: string[];
  input: string;
  onInputChange: (v: string) => void;
  onAdd: (slug: string) => void;
  onRemove: (slug: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function commitInput() {
    const val = input.trim().replace(/,/g, "");
    if (val && !slugs.includes(val)) onAdd(val);
    onInputChange("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !input && slugs.length > 0) {
      onRemove(slugs[slugs.length - 1]);
    }
  }

  return (
    <div
      className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-200 cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {slugs.map((slug) => (
        <span
          key={slug}
          className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-mono font-medium text-gray-700"
        >
          {slug}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(slug); }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitInput}
        placeholder={slugs.length === 0 ? "Type a slug and press Enter…" : ""}
        className="min-w-[140px] flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
      />
    </div>
  );
}

export default function SentrySettings() {
  const [configs, setConfigs] = useState<Record<string, SentryConfigOut>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forms, setForms] = useState<Record<string, PlatformFormState>>({
    phoenix: { auth_token: "", org_slug: "", project_slugs: [], slugInput: "" },
    kraken: { auth_token: "", org_slug: "", project_slugs: [], slugInput: "" },
  });
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({
    phoenix: "idle",
    kraken: "idle",
  });

  async function loadConfigs() {
    setLoading(true);
    setError("");
    try {
      const res = await statusApi.listSentryConfigs();
      const map: Record<string, SentryConfigOut> = {};
      for (const cfg of res.data as SentryConfigOut[]) {
        map[cfg.platform] = cfg;
      }
      setConfigs(map);
      // Pre-populate project slugs from existing config
      setForms((prev) => {
        const next = { ...prev };
        for (const cfg of res.data as SentryConfigOut[]) {
          next[cfg.platform] = {
            ...next[cfg.platform],
            org_slug: cfg.org_slug ?? "",
            project_slugs: slugsFromString(cfg.project_slugs),
          };
        }
        return next;
      });
    } catch {
      setError("Failed to load Sentry configurations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConfigs();
  }, []);

  function setField(platform: string, field: keyof PlatformFormState, value: string | string[]) {
    setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], [field]: value } }));
  }

  function addSlug(platform: string, slug: string) {
    setForms((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        project_slugs: [...prev[platform].project_slugs, slug],
        slugInput: "",
      },
    }));
  }

  function removeSlug(platform: string, slug: string) {
    setForms((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        project_slugs: prev[platform].project_slugs.filter((s) => s !== slug),
      },
    }));
  }

  async function handleSave(platform: string) {
    setSaveStates((s) => ({ ...s, [platform]: "saving" }));
    const form = forms[platform];
    const payload: { auth_token?: string; org_slug?: string; project_slugs?: string } = {};
    if (form.auth_token.trim()) payload.auth_token = form.auth_token.trim();
    if (form.org_slug.trim()) payload.org_slug = form.org_slug.trim();
    // Always send project_slugs so removals are persisted
    payload.project_slugs = form.project_slugs.join(",");
    try {
      const res = await statusApi.upsertSentryConfig(platform, payload);
      setConfigs((prev) => ({ ...prev, [platform]: res.data }));
      setForms((prev) => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          auth_token: "",
          project_slugs: slugsFromString(res.data.project_slugs),
        },
      }));
      setSaveStates((s) => ({ ...s, [platform]: "saved" }));
      setTimeout(() => setSaveStates((s) => ({ ...s, [platform]: "idle" })), 2000);
    } catch {
      setSaveStates((s) => ({ ...s, [platform]: "error" }));
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Sentry configuration</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure Sentry credentials per platform. Each platform can monitor multiple projects — add as many project slugs as needed (e.g. separate FE and BE projects for Kraken).
        </p>
      </div>

      {(["phoenix", "kraken"] as const).map((platform) => {
        const cfg = configs[platform];
        const form = forms[platform];
        const saveState = saveStates[platform];
        const isConfigured = cfg?.is_configured ?? false;
        const savedSlugs = slugsFromString(cfg?.project_slugs ?? null);

        return (
          <div key={platform} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-3.5">
              <h3 className="text-sm font-semibold capitalize text-gray-800">{platform}</h3>
              <div className="flex items-center gap-2">
                {savedSlugs.length > 0 && (
                  <span className="text-xs text-gray-400">
                    {savedSlugs.length} project{savedSlugs.length !== 1 ? "s" : ""}
                  </span>
                )}
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                    isConfigured
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-gray-200 bg-gray-50 text-gray-500"
                  }`}
                >
                  {isConfigured ? "Configured" : "Not configured"}
                </span>
              </div>
            </div>

            <div className="px-5 py-5 space-y-4">
              {/* Auth token */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Auth token</label>
                {cfg?.auth_token && (
                  <p className="mb-1.5 font-mono text-xs text-gray-500">{cfg.auth_token}</p>
                )}
                <input
                  type="password"
                  value={form.auth_token}
                  onChange={(e) => setField(platform, "auth_token", e.target.value)}
                  placeholder={cfg?.auth_token ? "Enter new token to replace" : "sntryu_…"}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>

              {/* Org slug */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Organisation slug</label>
                <input
                  type="text"
                  value={form.org_slug}
                  onChange={(e) => setField(platform, "org_slug", e.target.value)}
                  placeholder="e.g. my-org"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>

              {/* Project slugs — tag input */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Projects
                </label>
                <ProjectSlugInput
                  slugs={form.project_slugs}
                  input={form.slugInput}
                  onInputChange={(v) => setField(platform, "slugInput", v)}
                  onAdd={(slug) => addSlug(platform, slug)}
                  onRemove={(slug) => removeSlug(platform, slug)}
                />
                <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-400">
                  <Plus size={10} />
                  Type a project slug and press <kbd className="rounded border border-gray-200 bg-gray-100 px-1 font-mono text-[10px]">Enter</kbd> or <kbd className="rounded border border-gray-200 bg-gray-100 px-1 font-mono text-[10px]">,</kbd> to add. Add as many as needed (e.g. kraken-frontend, kraken-backend).
                </p>
              </div>

              {cfg?.updated_at && (
                <p className="text-xs text-gray-400">
                  Last updated {new Date(cfg.updated_at).toLocaleString()}
                </p>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => handleSave(platform)}
                  disabled={saveState === "saving"}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
                >
                  {saveState === "saving" && <Loader2 size={14} className="animate-spin" />}
                  {saveState === "saved" && <Check size={14} />}
                  {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : "Save"}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
